import React from 'react';
import {Button, Chip, IconButton} from '@mui/material';
import useSWR from 'swr';
import {deleteFetcher, getFetcher} from '../../utils/api/fetchers';
import useTableParams from "../../components/list/useTableParams";
import EntityTable from "../../components/list/EntityList";
import {PaginatedUserAccounts, UserAccount} from "../../schemas/user/userAccountSchemas";
import UserRole, {roleLabels} from "../../schemas/user/roles";
import buildUrl from "../../utils/api/buildUrl";
import {useAuth} from "../../utils/hooks/useAuth";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from '@mui/icons-material/Info';
import {useNavigate} from 'react-router-dom';
import {useNotification} from "../../utils/hooks/useNotification";
import {useConfirm} from "material-ui-confirm";

const Users = () => {
    const {hasPermission} = useAuth();
    const {sortKeys, sortDirections, page, rowsPerPage, filters, setPage} = useTableParams();
    const navigate = useNavigate();
    const confirm = useConfirm();
    const {showNotification} = useNotification();

    const url = buildUrl('/users', {
        page,
        size: rowsPerPage,
        sortBy: sortKeys,
        sortDirections,
        filters
    });
    const {data, mutate} = useSWR<PaginatedUserAccounts>(url, getFetcher, {keepPreviousData: true});
    const editPermission = hasPermission(UserRole.ADMIN);

    const handleCreateUser = () => {
        navigate('/users/create');
    };

    const handleDetails = (userId: number) => {
        navigate(`/users/${userId}`);
    };

    const handleDelete = (userId: number) => {
        confirm({
            description: `Opravdu chcete smazat tohoto uživatele?`,
            title: "Smazat uživatele",
            confirmationText: "Smazat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        })
            .then(() => {
                deleteFetcher(`/users/${userId}`).then(() => {
                    showNotification('Uživatel byl smazán', 'success');
                    if (data?.content.length === 1 && data.page.number > 0) {
                        setPage(data.page.number - 1);
                    } else {
                        mutate();
                    }
                }).catch(() => {
                    showNotification('Uživatele se nepodařilo smazat', 'error');
                });
            })
            .catch(() => {
            });
    };

    return (
        <EntityTable
            title="Uživatelé"
            controls={editPermission ? (
                [<Button key="add" variant="contained" color="success" onClick={handleCreateUser}>
                    Vytvořit uživatele
                </Button>]
            ) : []}
            headers={[
                {key: 'firstName', label: 'Jméno', sortable: true, filtered: true},
                {key: 'lastName', label: 'Příjmení', sortable: true, filtered: true},
                {key: 'username', label: 'Uživatelské jméno', sortable: true, filtered: true},
                {key: 'email', label: 'Email', sortable: true, filtered: true},
                {key: 'phoneNumber', label: 'Telefon', filtered: true},
                {
                    key: 'roles',
                    label: 'Role',
                    render: (roles: UserRole[]) => roles.map((role: UserRole) => roleLabels[role]).join(', ')
                },
                {
                    key: 'enabled',
                    label: 'Aktivní',
                    sortable: true,
                    filtered: true,
                    filterConfig: {
                        type: 'select',
                        options: [
                            {value: "", label: "Vše"},
                            {value: "True", label: "Ano"},
                            {value: "False", label: "Ne"}
                        ]
                    },
                    render: (enabled) => (
                        <Chip label={enabled ? 'Ano' : 'Ne'} color={enabled ? 'success' : 'error'}/>
                    ),
                },
                ...(editPermission ? [
                    {
                        key: 'controls',
                        render: (account: UserAccount) => (
                            <div>
                                <IconButton color="info" onClick={() => handleDetails(account.id)}
                                            title="Detaily a úprava">
                                    <EditIcon/>
                                </IconButton>
                                <IconButton color="error" onClick={() => handleDelete(account.id)} title="Smazat">
                                    <DeleteIcon/>
                                </IconButton>
                            </div>
                        )
                    }
                ] : [
                    {
                        key: 'controls',
                        label: 'Detaily',
                        render: (account: UserAccount) => (
                            <IconButton color="info" onClick={() => handleDetails(account.id)} title="Detaily">
                                <InfoIcon/>
                            </IconButton>
                        )
                    }
                ])
            ]}
            entities={data?.content ?? []}
            entitiesCount={data?.page.totalElements ?? 0}
            indexes
        />
    );
};

export default Users;
